import { SearchIcon } from "@heroicons/react/solid";
import { useCombobox } from "downshift";
import { debounce } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import HotspotComponent from "../../../components/Hotspot";
import {
  convertEtlHotspotSearchResponse,
  HotspotsSearchResponse
} from "../../../context/hotspots/HotspotsProvider";
import classNames from "../../../utils/classNames";
import { h3ToCenterChildMemoized } from "../../../utils/memoizedMethods";
import { EtlHotspot } from "../../../utils/types";
import useMapCenter from "../../../utils/useMapCenter";

interface Props extends RouteComponentProps<{}> {}

const SelectWalletByHotspotPage: React.FC<Props> = () => {
  const history = useHistory();
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const [fetchedHotspots, setFetchedHotspots] = useState<EtlHotspot[]>([]);

  const handleHotspotClick = (walletAddress: string) => {
    window.scrollTo(0, 0);
    history.push(`/workspace/wallets/verify/${walletAddress}/confirm-wallet`);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchResults = useCallback(
    debounce(async (searchInput: string | undefined) => {
      if (!searchInput || searchInput.trim().length <= 4) {
        setFetchedHotspots([]);
        return;
      }

      const findHotspotsUrl = stringifyUrl({
        url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean`,
        query: {
          name: searchInput,
          limit: 5,
          ...(centerInH3HexRes4
            ? {
                proximity_hex: h3ToCenterChildMemoized(centerInH3HexRes4, 12)
              }
            : {})
        }
      });

      fetch(findHotspotsUrl)
        .then((res) => res.json())
        .then((res: HotspotsSearchResponse) => {
          setFetchedHotspots(convertEtlHotspotSearchResponse(res.data) || []);
        });
    }, 200),
    [centerInH3HexRes4]
  );

  const {
    getInputProps,
    getComboboxProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useCombobox<EtlHotspot>({
    isOpen: true,
    items: fetchedHotspots,
    onSelectedItemChange: ({ selectedItem }) => {
      history.push(
        `/workspace/wallets/verify/${selectedItem?.owner}/confirm-wallet`
      );
    },
    onInputValueChange: ({ inputValue }) => {
      debouncedFetchResults(inputValue);
    }
  });

  return (
    <>
      <header className="relative py-10 text-center">
        <h1 className="text-xl sm:text-3xl font-bold text-white">
          Identify a wallet to verify
        </h1>
      </header>

      <main className="max-w-screen-sm mx-auto bg-white dark:bg-gray-800 dark:text-white rounded-lg shadow divide-y divide-gray-200 dark:divide-gray-500">
        <div className="p-2">
          <div
            className="h-8 w-full text-gray-400 group focus-within:text-gray-600 flex items-center"
            {...getComboboxProps({}, { suppressRefError: true })}
          >
            <div className="flex-none flex items-center">
              <SearchIcon className="h-5 w-5" />
            </div>

            <input
              className="block w-full h-full p-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent dark:bg-gray-800 dark:text-white"
              autoFocus
              placeholder="Find hotspot"
              type="search"
              {...getInputProps({}, { suppressRefError: true })}
            />
          </div>
        </div>

        {fetchedHotspots.length !== 0 && (
          <div {...getMenuProps({}, { suppressRefError: true })}>
            {fetchedHotspots.map((hotspot, index) => (
              <div
                key={hotspot.id}
                className={classNames(
                  highlightedIndex === index &&
                    "ring-2 ring-inset ring-hotspotty-400"
                )}
                {...getItemProps({ item: hotspot, index })}
              >
                <HotspotComponent
                  hotspot={hotspot}
                  handleOnClick={handleHotspotClick}
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

export default SelectWalletByHotspotPage;
